import { useState } from 'react';
import { Stack, TextField, Button, Typography, Divider, Box } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PdiComment } from '../../../api/installations.api';

interface Props {
  /** Stable key for the react-query cache, e.g. 'installation' | 'service-ticket'. */
  scope: string;
  entityId: number;
  list: (id: number) => Promise<PdiComment[]>;
  add: (id: number, comment: string) => Promise<PdiComment>;
  /** Whether the current user can post a comment. */
  canComment: boolean;
}

/** Shared comments thread used by PDI installations and service tickets. */
export function PdiCommentsThread({ scope, entityId, list, add, canComment }: Props) {
  const qc = useQueryClient();
  const key = ['pdi-comments', scope, entityId];
  const [text, setText] = useState('');
  const { data: comments } = useQuery({ queryKey: key, queryFn: () => list(entityId), enabled: entityId > 0 });
  const addMut = useMutation({
    mutationFn: () => add(entityId, text.trim()),
    onSuccess: () => { setText(''); qc.invalidateQueries({ queryKey: key }); },
  });

  return (
    <Box>
      <Divider textAlign="left" sx={{ my: 1 }}>Comments</Divider>
      <Stack spacing={1} mb={1}>
        {(comments ?? []).map((c) => (
          <Box key={c.id} sx={{ borderLeft: '3px solid', borderColor: 'divider', pl: 1 }}>
            <Typography variant="body2">{c.comment}</Typography>
            <Typography variant="caption" color="text.secondary">{c.authorLabel ?? 'System'} · {new Date(c.createdAt).toLocaleString()}</Typography>
          </Box>
        ))}
        {(comments ?? []).length === 0 && <Typography variant="body2" color="text.secondary">No comments yet.</Typography>}
      </Stack>
      {canComment && (
        <Stack direction="row" spacing={1}>
          <TextField size="small" label="Add a comment" value={text} onChange={(e) => setText(e.target.value)} fullWidth multiline maxRows={3} />
          <Button variant="outlined" disabled={!text.trim() || addMut.isPending} onClick={() => addMut.mutate()}>Post</Button>
        </Stack>
      )}
    </Box>
  );
}
